// Status tones — the one sanctioned way to express "good / careful / bad /
// informational" in this app.
//
// Why this exists: an audit on 2026-07-30 found 193 hardcoded Tailwind palette
// classes across 24 files. `emerald` and `green` both meant success, `orange`
// and `amber` both meant warning, and because they were raw palette values NONE
// of them responded to `.dark` — so every status pill in the app was unreadable
// in dark mode even though a full set of dark tokens existed.
//
// The fix is not "pick better colours", it is removing the decision from the
// call site. Components below take a semantic `tone`; no route should ever name
// a colour again.

import * as React from "react";
import { cn } from "@/lib/utils";

export type Tone = "success" | "warning" | "danger" | "info" | "neutral";

const TONE_CLASS: Record<Tone, string> = {
  success: "bg-tone-success-bg text-tone-success-fg ring-tone-success-border",
  warning: "bg-tone-warning-bg text-tone-warning-fg ring-tone-warning-border",
  danger: "bg-tone-danger-bg text-tone-danger-fg ring-tone-danger-border",
  info: "bg-tone-info-bg text-tone-info-fg ring-tone-info-border",
  neutral: "bg-tone-neutral-bg text-tone-neutral-fg ring-tone-neutral-border",
};

/**
 * Solid fills for the meter bar. Written out in full rather than composed as
 * `bg-tone-${tone}-fg`: Tailwind scans source for STATIC class strings, so an
 * interpolated name is never generated and the bar silently renders unstyled.
 */
const TONE_FILL: Record<Tone, string> = {
  success: "bg-tone-success-fg",
  warning: "bg-tone-warning-fg",
  danger: "bg-tone-danger-fg",
  info: "bg-tone-info-fg",
  neutral: "bg-foreground",
};

/**
 * Small inline status label. Use for a row's state, never for an action:
 * a pill that looks clickable but is not is a worse lie than a plain word.
 */
export function StatusPill({
  tone = "neutral",
  children,
  className,
}: {
  tone?: Tone;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        TONE_CLASS[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

/**
 * Block-level callout. Same tones, more room. `icon` is passed rather than
 * chosen here so the caller can match it to the specific message.
 */
export function Callout({
  tone = "info",
  icon: Icon,
  title,
  children,
  className,
}: {
  tone?: Tone;
  icon?: React.ComponentType<{ className?: string }>;
  title?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg p-4 ring-1 ring-inset",
        TONE_CLASS[tone],
        className
      )}
    >
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 opacity-80" />}
      <div className="min-w-0 text-sm">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "opacity-90")}>{children}</div>}
      </div>
    </div>
  );
}

/**
 * Usage meter. Kept here rather than in a route because "how full is this
 * quota" is a system-wide idea, and because the at-limit colour must come from
 * the same tone scale as everything else.
 */
export function Meter({
  value,
  max,
  tone,
}: {
  value: number;
  max: number;
  /** Overrides the automatic tone. Use when 0 means "paused", not "full". */
  tone?: Tone;
}) {
  const unlimited = max >= 100000;
  const none = max === 0;
  const atLimit = !unlimited && max > 0 && value >= max;
  const pct = unlimited ? 8 : none ? 100 : Math.min(100, Math.round((value / max) * 100));
  const resolved: Tone = tone ?? (atLimit || none ? "warning" : "neutral");

  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
      <div
        className={cn("h-full rounded-full transition-all duration-500", TONE_FILL[resolved])}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
